import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BsModal from 'react-bootstrap/Modal';
import moment from 'moment';
import { FaExclamationTriangle, FaFlagCheckered } from 'react-icons/fa';
import ContractService from '../../services/contract.service';
import { useLabels } from '../../hooks/useLabels';
import { toast } from '../../toasts';
import { getError } from '../../utils';
import { Button, FormField, controlClass } from '../EntityList';

const today = () => moment.utc().format('YYYY-MM-DD');

/** A close date before today cancels the dues (and their payments) generated after it. */
export const isRetroactiveClose = (closeDate) =>
	Boolean(closeDate) && moment.utc(closeDate).isBefore(moment.utc(today()));

/**
 * Finalizar contrato — single-contract close (PUT /contracts/closingDate/:id).
 * Replaces the legacy two-step modals on the contract detail page: the
 * retroactive-date warning now shows inline, as soon as a past date is picked,
 * instead of in a second dialog.
 */
function CloseContractModal({ show, contract, onClose, onClosed }) {
	const { t } = useLabels();
	const [closeDate, setCloseDate] = useState(today());
	const [saving, setSaving] = useState(false);
	const submitting = useRef(false);

	useEffect(() => {
		if (show) setCloseDate(today());
	}, [show]);

	const retroactive = isRetroactiveClose(closeDate);
	const canSubmit = Boolean(closeDate) && !saving;

	const handleConfirm = async () => {
		if (!canSubmit || submitting.current) return;
		submitting.current = true;
		setSaving(true);
		try {
			await ContractService.close(contract._id, { closeDate, status: 'closed' });
			toast.success(t('msg.contractClosed'));
			onClosed();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			submitting.current = false;
			setSaving(false);
		}
	};

	return (
		<BsModal
			show={show}
			onHide={saving ? undefined : onClose}
			centered
			backdrop={saving ? 'static' : true}
			keyboard={!saving}
			dialogClassName='tw:max-w-[480px]'
			contentClassName='entity-list-page el-modal tw:rounded-[14px] tw:border-0 tw:overflow-hidden tw:shadow-overlay tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
		>
			<BsModal.Header
				closeButton={!saving}
				className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
			>
				<div className='tw:flex tw:items-start tw:gap-3'>
					<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-accent-soft tw:text-accent'>
						<FaFlagCheckered />
					</span>
					<div>
						<BsModal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
							{t('title.confirmCloseContract')}
						</BsModal.Title>
						{contract?.customId && <p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>{contract.customId}</p>}
					</div>
				</div>
			</BsModal.Header>

			<BsModal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
				<p className='tw:mb-0 tw:text-[13px] tw:text-ink-2'>{t('msg.closeContractIntro')}</p>
				<FormField id='close-contract-date' label={t('form.closeDate')} required>
					<input
						id='close-contract-date'
						type='date'
						value={closeDate}
						onChange={(event) => setCloseDate(event.target.value)}
						disabled={saving}
						className={`${controlClass} tw:w-full tw:px-3`}
					/>
				</FormField>
				{retroactive && (
					<div
						role='alert'
						className='tw:flex tw:items-start tw:gap-2.5 tw:rounded-[7px] tw:border tw:border-warn tw:bg-warn-soft tw:px-3 tw:py-2.5 tw:text-[12px] tw:text-ink-1'
					>
						<span className='tw:mt-0.5 tw:shrink-0 tw:text-warn'>
							<FaExclamationTriangle />
						</span>
						<span>{t('msg.closeContractWarning')}</span>
					</div>
				)}
			</BsModal.Body>

			<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
				<Button variant='default' onClick={onClose} disabled={saving}>
					{t('action.goBack')}
				</Button>
				<Button
					variant='primary'
					icon={<FaFlagCheckered />}
					disabled={!canSubmit}
					className={!canSubmit ? 'tw:opacity-50' : ''}
					onClick={handleConfirm}
				>
					{retroactive ? t('action.closeContractRetroactive') : t('action.closeContract')}
				</Button>
			</BsModal.Footer>
		</BsModal>
	);
}

CloseContractModal.propTypes = {
	show: PropTypes.bool.isRequired,
	contract: PropTypes.shape({ _id: PropTypes.string, customId: PropTypes.string }),
	onClose: PropTypes.func.isRequired,
	onClosed: PropTypes.func.isRequired,
};

export default CloseContractModal;
